import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { FILE_REQUEST_COPY } from '@/lib/fileRequestCopy';

/**
 * File-request category labels, fetched from the API — the ONLY place the
 * frontend knows about them.
 *
 * These strings existed in three components and two backend modules, with nothing
 * enforcing agreement. That is worse than duplicated code: copy drift breaks
 * nothing, throws nothing, and shows up as a client reading one wording in the app
 * and a different one in an email about the same request. The canonical list lives
 * in backend/app/constants.py and is served by GET /file-requests/categories.
 *
 * Cached at module scope and fetched once per page load. The list changes only
 * when someone edits the constant and redeploys, so re-fetching per mount would be
 * three requests to learn four strings that cannot have changed.
 */
let _cache = null;
let _inflight = null;

async function load() {
  if (_cache) return _cache;
  if (!_inflight) {
    _inflight = api.get('/file-requests/categories')
      .then(r => { _cache = r.data || []; return _cache; })
      .catch(() => [])          // degrade to the fallback label, never blow up a panel
      .finally(() => { _inflight = null; });
  }
  return _inflight;
}

export function useFileRequestCategories() {
  const [categories, setCategories] = useState(_cache || []);
  useEffect(() => {
    let cancelled = false;
    load().then(list => { if (!cancelled) setCategories(list); });
    return () => { cancelled = true; };
  }, []);
  return categories;
}

/** Label for a stored category value. `fallback` covers records written before a
 *  rename — old rows degrade to generic text rather than rendering a raw key. */
export function labelFor(categories, value, fallback = 'We need something') {
  return (categories.find(c => c.value === value) || {}).label || fallback;
}

/* ── COPY MAP ──────────────────────────────────────────────────────────────
 *
 * The panels hold no copy inline. Every sentence comes from `fileRequestCopy.js`,
 * the JS half of a map whose canonical side is `backend/app/file_request_copy.py`;
 * `backend/tests/test_file_request_copy_parity.py` asserts the two agree.
 *
 * It is a STATIC import, not a fetch, on purpose. An earlier draft served the map
 * from the API so the two sides could not drift at all — but that bought a
 * build-time guarantee with a runtime dependency, and a stale or failing backend
 * would have rendered three panels with blank labels. The test catches the same
 * drift before the code ships, with nothing to fail in front of a client.
 *
 * There is deliberately NO frame-plus-noun accessor. `proseFor` is gone: it
 * existed to drop a category noun into "You asked for ___", which is the
 * construction that produced "You asked for a question about the files".
 */
export function useFileRequestCopy() {
  return FILE_REQUEST_COPY;
}

/** Resolve one entry for a category. `_default` covers the three file-shaped
 *  categories; a category key overrides it. An unknown key returns '' rather than
 *  throwing — a missing label should not take a whole panel down with it, and the
 *  parity test is what stops a missing key reaching here in the first place. */
export function copyFor(copy, key, category = '') {
  const entry = (copy || FILE_REQUEST_COPY)[key];
  if (!entry) return '';
  return entry[category] || entry._default || '';
}

/** Split a template on {placeholders} so a surface can supply its own emphasis.
 *  The SENTENCE is shared with the email; the markup around the emphasised words
 *  is not, because a Tailwind class and an inline style cannot be the same string.
 *  Returns an array of strings and nodes, safe to render directly in JSX. */
export function interpolate(template, nodes) {
  if (!template) return [];
  return template.split(/(\{[a-z_]+\})/i).map((part, i) => {
    const m = part.match(/^\{([a-z_]+)\}$/i);
    if (!m) return part;
    const node = nodes[m[1]];
    return node === undefined ? part : <span key={i}>{node}</span>;
  });
}

export default useFileRequestCategories;
